import { embeddedPngBytes } from './embeddedPng.js';

/** Municipality-owned design, snapshotted when a notification is queued. */
export const PRINTABLE_LICENSE_DEFAULTS = Object.freeze({
  template: 'municipal_v1',
  ink: '#26343b',
  accent: '#b85b32',
  muted: '#5c676b',
  headerLogo: '',
  title: 'Short-Term Rental License',
  statement: 'The property identified below is licensed for short-term rental use, subject to applicable municipal requirements and the conditions of its license.',
  postingInstructions: 'Please display this certificate in a location visible to guests.',
  localContactNameFieldId: '',
  localContactPhoneFieldId: ''
});

export function printableLicenseDefaults() {
  return { ...PRINTABLE_LICENSE_DEFAULTS };
}

export function normalizePrintableLicense(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const result = { ...PRINTABLE_LICENSE_DEFAULTS };
  for (const key of Object.keys(result)) {
    if (typeof value[key] === 'string') result[key] = value[key].trim();
  }
  return result;
}

/** Prefer the shared design; use the first legacy rule's design until migrated. */
export function municipalityPrintableLicense(data = {}) {
  const legacy = data.licenseStatusEmails?.rules?.find(rule => rule?.certificate)?.certificate;
  return normalizePrintableLicense(data.printableLicense || legacy) || printableLicenseDefaults();
}

export function validatePrintableLicense(value, { cityId } = {}) {
  if (value === undefined || value === null) return [];
  if (typeof value !== 'object' || Array.isArray(value)) return ['Printable license settings must be an object.'];
  const errors = [];
  const settings = normalizePrintableLicense(value);
  if (!['municipal_v1', 'scarborough_v1'].includes(settings.template)) errors.push('Unknown printable license template.');
  if (settings.template === 'scarborough_v1' && cityId && cityId !== 'scarborough_me') errors.push('The Scarborough template is only available to Scarborough.');
  for (const key of ['ink', 'accent', 'muted']) {
    if (value[key] !== undefined && (typeof value[key] !== 'string' || !/^#[0-9a-f]{6}$/i.test(value[key]))) {
      errors.push(`Printable license ${key} must be a six-digit hex color, such as #26343b.`);
    }
  }
  if (value.headerLogo !== undefined && typeof value.headerLogo !== 'string') errors.push('Printable license header logo must be an embedded PNG.');
  if (value.headerLogo && embeddedPngBytes(value.headerLogo) === null) errors.push('Printable license header logo must be an embedded PNG no larger than 100 KB.');
  for (const [key, limit] of [['title', 100], ['statement', 700], ['postingInstructions', 200], ['localContactNameFieldId', 100], ['localContactPhoneFieldId', 100]]) {
    if (value[key] !== undefined && typeof value[key] !== 'string') errors.push(`Printable license ${key} must be text.`);
    if (settings[key].length > limit) errors.push(`Printable license ${key} must be ${limit} characters or fewer.`);
  }
  if (!settings.title || !settings.statement) errors.push('Printable license title and statement are required.');
  return errors;
}

/** A term is identified by its expiry date (or issue date for non-expiring licenses). */
export function licenseEmailTermKey(data = {}) {
  const value = data.expiresOn || data.issuedOn || data.dateIssued;
  const date = value?.toDate ? value.toDate() : value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toISOString().slice(0, 10) : 'undated';
}
